import { useEffect, useRef } from 'react';

// Foco preso para modais e diálogos (WCAG 2.1.2, 2.4.3):
//   - ao abrir, leva o foco para dentro (o elemento de `focoInicial`, o primeiro
//     com [data-autofocus] / autoFocus, o primeiro focável ou a própria caixa);
//   - Tab e Shift+Tab circulam só dentro da caixa;
//   - Esc chama `aoFechar` (se houver);
//   - ao fechar, devolve o foco ao elemento que estava focado antes (quem abriu).
//
// Uso:
//   const caixaRef = useFocoPreso(aberto, { aoFechar: fechar });
//   <div ref={caixaRef} role="dialog" aria-modal="true" aria-labelledby="titulo-x" tabIndex={-1}>
//
// Modais aninhados (um diálogo de confirmação sobre o painel da tarefa): só o
// do topo da pilha reage ao teclado, então o Esc fecha um de cada vez. Um
// componente de dentro que trate o Esc sozinho (fechar um menu, cancelar uma
// edição) chama e.preventDefault() e o modal não fecha.
const pilha = [];
let proximoId = 1;

const SELETOR_FOCAVEL = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const elementosFocaveis = (caixa) => {
  if (!caixa) return [];
  return Array.from(caixa.querySelectorAll(SELETOR_FOCAVEL)).filter((el) => {
    if (el.closest('[inert]') || el.getAttribute('aria-hidden') === 'true') return false;
    // Visível: com caixa de layout e sem visibility: hidden.
    if (!el.getClientRects().length) return false;
    return window.getComputedStyle(el).visibility !== 'hidden';
  });
};

const useFocoPreso = (ativo, { aoFechar, focoInicial, devolverFoco = true } = {}) => {
  const caixaRef = useRef(null);
  const aoFecharRef = useRef(aoFechar);
  aoFecharRef.current = aoFechar;
  const focoInicialRef = useRef(focoInicial);
  focoInicialRef.current = focoInicial;

  useEffect(() => {
    if (!ativo) return undefined;
    const id = proximoId++;
    pilha.push(id);
    const anterior = document.activeElement;

    const focarDentro = () => {
      const caixa = caixaRef.current;
      if (!caixa) return;
      // Um autoFocus do próprio conteúdo já resolveu.
      if (caixa.contains(document.activeElement) && document.activeElement !== caixa) return;
      const alvoInicial = focoInicialRef.current?.current
        || caixa.querySelector('[data-autofocus]')
        || elementosFocaveis(caixa)[0]
        || caixa;
      if (alvoInicial === caixa && !caixa.hasAttribute('tabindex')) caixa.setAttribute('tabindex', '-1');
      alvoInicial.focus({ preventScroll: alvoInicial === caixa });
    };
    // Depois do commit e de efeitos dos filhos (que podem focar um campo).
    const temporizador = window.setTimeout(focarDentro, 0);

    const aoTeclar = (e) => {
      if (pilha[pilha.length - 1] !== id) return;
      const caixa = caixaRef.current;
      if (!caixa) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (e.defaultPrevented || !aoFecharRef.current) return;
        e.preventDefault();
        aoFecharRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const focaveis = elementosFocaveis(caixa);
      if (!focaveis.length) {
        e.preventDefault();
        caixa.focus();
        return;
      }
      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];
      const atual = document.activeElement;
      if (!caixa.contains(atual)) {
        e.preventDefault();
        (e.shiftKey ? ultimo : primeiro).focus();
      } else if (e.shiftKey && (atual === primeiro || atual === caixa)) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && atual === ultimo) {
        e.preventDefault();
        primeiro.focus();
      }
    };
    document.addEventListener('keydown', aoTeclar);

    return () => {
      window.clearTimeout(temporizador);
      document.removeEventListener('keydown', aoTeclar);
      const i = pilha.indexOf(id);
      if (i >= 0) pilha.splice(i, 1);
      if (devolverFoco && anterior && anterior !== document.body && typeof anterior.focus === 'function' && document.contains(anterior)) {
        anterior.focus({ preventScroll: true });
      }
    };
  }, [ativo, devolverFoco]);

  return caixaRef;
};

export default useFocoPreso;
